import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  isLiveTier,
  overlappingTier,
  replaceTierInput,
  tierInput,
  type AdminFormat,
  type AdminTier,
  type ReplaceTierInput,
  type TierInput,
} from '@gg/shared';
import { Badge, Button, Card, ConfirmDialog, Table, Td, TextField, Th } from '@/components/ui';
import { adminApiErrorKey, adminIssueKey } from '@/lib/adminErrors';
import { ApiRequestError } from '@/lib/api';
import { eventLocalToIso, formatDateTime, isoToEventLocal } from '@/lib/dates';
import { centsToEuroInput, formatPrice, parseEurosToCents } from '@/lib/i18n';
import { useCreateTier, useDeleteTier, useReplaceTier, useUpdateTier } from '@/lib/queries';

type Mode = { kind: 'add' } | { kind: 'edit' | 'replace'; tier: AdminTier };
type Values = {
  name: string;
  price: string;
  startsAt: string;
  endsAt: string;
  capacity: string;
};
type Pending = { lines: string[]; run: () => Promise<unknown> };

const intOrNull = (s: string) =>
  s.trim() === '' ? null : /^\d+$/.test(s.trim()) ? Number(s) : NaN;

/** "du 30 oct. 2026, 08:00 au 1 janv. 2027, 00:00", or the open-ended variants. */
function tierWindow(t: TFunction, startsAt: string | null, endsAt: string | null) {
  const from = startsAt && formatDateTime(startsAt);
  const until = endsAt && formatDateTime(endsAt);
  if (from && until) return t('admin.tiers.window.both', { from, until });
  if (from) return t('admin.tiers.window.from', { from });
  if (until) return t('admin.tiers.window.until', { until });
  return t('admin.tiers.window.always');
}

/** When a tier applies, relative to now: live, later, or never. */
function timing(t: TFunction, w: { startsAt: string | null; endsAt: string | null }) {
  const now = new Date();
  if (isLiveTier(w, now)) return t('admin.tiers.confirm.liveNow');
  if (w.startsAt && new Date(w.startsAt) > now)
    return t('admin.tiers.confirm.future', { date: formatDateTime(w.startsAt) });
  return t('admin.tiers.confirm.past');
}

/** A-01: a format's pricing tiers. Frozen tiers change price through "Change the price". */
export function AdminTiers({ format }: { format: AdminFormat }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<string | null>(null);
  const remove = useDeleteTier(format.id);
  const now = new Date();

  const run = async () => {
    if (!pending) return;
    setError(null);
    try {
      await pending.run();
      setMode(null);
    } catch (e) {
      const details = (e instanceof ApiRequestError ? e.details : null) as { name?: string } | null;
      setError(t(`admin.error.${adminApiErrorKey(e)}`, { name: details?.name }));
    } finally {
      setPending(null);
    }
  };

  const askDelete = (tier: AdminTier) =>
    setPending({
      lines: [
        t('admin.tiers.confirm.delete', { name: tier.name, price: formatPrice(tier.priceCents) }),
      ],
      run: () => remove.mutateAsync(tier.id),
    });

  return (
    <Card className="gap-4!">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold">{t('admin.tiers.title')}</h2>
        {!mode && (
          <Button
            variant="secondary"
            size="md"
            className="w-auto!"
            onClick={() => setMode({ kind: 'add' })}
          >
            {t('admin.tiers.add')}
          </Button>
        )}
      </div>
      <p className="text-sm text-ink-muted">{t('admin.tiers.intro')}</p>

      {format.tiers.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('admin.tiers.empty')}</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('admin.tiers.col.name')}</Th>
              <Th className="text-right">{t('admin.tiers.col.price')}</Th>
              <Th>{t('admin.tiers.col.from')}</Th>
              <Th>{t('admin.tiers.col.until')}</Th>
              <Th className="text-right">{t('admin.tiers.col.capacity')}</Th>
              <Th className="text-right">{t('admin.tiers.col.used')}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {format.tiers.map((tier) => {
              const frozen = tier.registrations > 0;
              const ended = tier.endsAt !== null && new Date(tier.endsAt) <= now;
              return (
                <tr key={tier.id}>
                  <Td>
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{tier.name}</span>
                      {tier.live && <Badge tone="success">{t('admin.tiers.live')}</Badge>}
                      {frozen && <Badge>{t('admin.tiers.frozen')}</Badge>}
                    </span>
                  </Td>
                  <Td className="text-right font-semibold">{formatPrice(tier.priceCents)}</Td>
                  <Td className="whitespace-nowrap">
                    {tier.startsAt ? formatDateTime(tier.startsAt) : t('admin.tiers.open')}
                  </Td>
                  <Td className="whitespace-nowrap">
                    {tier.endsAt ? formatDateTime(tier.endsAt) : t('admin.tiers.open')}
                  </Td>
                  <Td className="text-right">{tier.capacity ?? t('admin.tiers.open')}</Td>
                  <Td className="text-right">{tier.registrations}</Td>
                  <Td className="whitespace-nowrap text-right">
                    {!mode && !frozen && (
                      <span className="flex justify-end gap-3">
                        <button
                          type="button"
                          className="cursor-pointer font-semibold text-accent"
                          onClick={() => setMode({ kind: 'edit', tier })}
                        >
                          {t('admin.tiers.edit')}
                        </button>
                        <button
                          type="button"
                          className="cursor-pointer text-ink-muted"
                          onClick={() => askDelete(tier)}
                        >
                          {t('admin.tiers.delete')}
                        </button>
                      </span>
                    )}
                    {!mode && frozen && !ended && (
                      <button
                        type="button"
                        className="cursor-pointer font-semibold text-accent"
                        onClick={() => setMode({ kind: 'replace', tier })}
                      >
                        {t('admin.tiers.replace')}
                      </button>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      {mode && (
        <TierForm
          key={mode.kind === 'add' ? 'add' : `${mode.kind}-${mode.tier.id}`}
          format={format}
          mode={mode}
          onCancel={() => {
            setMode(null);
            setError(null);
          }}
          onConfirm={(p) => {
            setError(null);
            setPending(p);
          }}
        />
      )}

      <ConfirmDialog
        open={pending !== null}
        title={t('admin.tiers.confirm.title')}
        confirmLabel={t('admin.confirm.apply')}
        cancelLabel={t('admin.confirm.cancel')}
        onConfirm={run}
        onCancel={() => setPending(null)}
      >
        <ul className="m-0 flex list-disc flex-col gap-1 pl-5">
          {pending?.lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      </ConfirmDialog>
    </Card>
  );
}

function TierForm({
  format,
  mode,
  onCancel,
  onConfirm,
}: {
  format: AdminFormat;
  mode: Mode;
  onCancel: () => void;
  onConfirm: (p: Pending) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateTier(format.id);
  const update = useUpdateTier(format.id);
  const replace = useReplaceTier(format.id);
  const tier = mode.kind === 'add' ? null : mode.tier;
  const replacing = mode.kind === 'replace';
  const { register, handleSubmit, setError, formState } = useForm<Values>({
    defaultValues: {
      name: tier?.name ?? '',
      // Replacing: the new price is typed afresh; the old one is shown in the intro.
      price: tier && !replacing ? centsToEuroInput(tier.priceCents) : '',
      startsAt: replacing
        ? isoToEventLocal(new Date().toISOString())
        : isoToEventLocal(tier?.startsAt ?? null),
      endsAt: isoToEventLocal(tier?.endsAt ?? null),
      capacity: tier?.capacity ? String(tier.capacity) : '',
    },
  });
  const err = (f: keyof Values) => formState.errors[f]?.message;

  const onSubmit = (v: Values) => {
    const body = {
      name: v.name,
      priceCents: parseEurosToCents(v.price) ?? NaN,
      startsAt: v.startsAt ? eventLocalToIso(v.startsAt) : null,
      endsAt: v.endsAt ? eventLocalToIso(v.endsAt) : null,
      capacity: intOrNull(v.capacity),
    };
    const fieldOf = (k: string): keyof Values =>
      k === 'priceCents' ? 'price' : k === 'switchAt' ? 'startsAt' : (k as keyof Values);
    const parsed = replacing
      ? replaceTierInput.safeParse({ ...body, switchAt: body.startsAt })
      : tierInput.safeParse(body);
    if (!parsed.success) {
      for (const issue of parsed.error.issues)
        setError(fieldOf(String(issue.path[0])), {
          message: t(`admin.error.${adminIssueKey(issue)}`),
        });
      return;
    }

    // Same overlap rule as the Worker, checked here to point at the field before confirming.
    const others = format.tiers.filter((x) => x.id !== tier?.id);
    const clash = overlappingTier({ startsAt: body.startsAt, endsAt: body.endsAt }, others);
    if (clash) {
      setError('startsAt', { message: t('admin.error.overlap', { name: clash.name }) });
      return;
    }

    const price = formatPrice(body.priceCents);
    const span = tierWindow(t, body.startsAt, body.endsAt);
    if (replacing && tier) {
      const data = parsed.data as ReplaceTierInput;
      onConfirm({
        lines: [
          t('admin.tiers.confirm.replace', {
            old: tier.name,
            oldPrice: formatPrice(tier.priceCents),
            date: formatDateTime(data.switchAt),
            name: data.name,
            price,
            window: span,
          }),
          timing(t, { startsAt: data.switchAt, endsAt: data.endsAt }),
        ],
        run: () => replace.mutateAsync({ tierId: tier.id, body: data }),
      });
      return;
    }
    const data = parsed.data as TierInput;
    onConfirm({
      lines: [
        t('admin.tiers.confirm.window', { name: data.name, price, window: span }),
        timing(t, data),
      ],
      run: () =>
        tier ? update.mutateAsync({ tierId: tier.id, body: data }) : create.mutateAsync(data),
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-4 rounded-control bg-surface p-4"
    >
      <h3 className="text-base font-bold">
        {mode.kind === 'add'
          ? t('admin.tiers.form.addTitle')
          : t(`admin.tiers.form.${mode.kind}Title`, { name: mode.tier.name })}
      </h3>
      {replacing && tier && (
        <p className="text-sm text-ink-muted">
          {t('admin.tiers.form.replaceIntro', {
            name: tier.name,
            price: formatPrice(tier.priceCents),
          })}
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <TextField label={t('admin.tiers.form.name')} error={err('name')} {...register('name')} />
        <TextField
          label={t('admin.tiers.form.price')}
          inputMode="decimal"
          error={err('price')}
          {...register('price')}
        />
        <TextField
          type="datetime-local"
          label={t(replacing ? 'admin.tiers.form.switchAt' : 'admin.tiers.form.startsAt')}
          hint={replacing ? undefined : t('admin.tiers.form.openHint')}
          error={err('startsAt')}
          {...register('startsAt')}
        />
        <TextField
          type="datetime-local"
          label={t('admin.tiers.form.endsAt')}
          hint={t('admin.tiers.form.openHint')}
          error={err('endsAt')}
          {...register('endsAt')}
        />
        <TextField
          label={t('admin.tiers.form.capacity')}
          hint={t('admin.tiers.form.capacityHint')}
          inputMode="numeric"
          error={err('capacity')}
          {...register('capacity')}
        />
      </div>
      <div className="flex gap-3">
        <Button type="submit" size="md" className="w-auto!">
          {t('admin.tiers.form.save')}
        </Button>
        <Button variant="ghost" size="md" className="w-auto!" onClick={onCancel}>
          {t('admin.tiers.cancel')}
        </Button>
      </div>
    </form>
  );
}
